import { MessageSquareText, PhoneCall, KeyRound } from "lucide-react";
import { homeLabel } from "@/lib/home";

/* ---- helpers ---- */
export function fmtPhone(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
}
export const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};
export const firstName = (p) => (p?.first_name || (p?.name || "").split(" ")[0] || "").trim() || "them";
export const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };
export const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return ""; } };
export const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};
export const personaOf = (p) => p?.relationship || (p?.is_owner ? "Owner" : "Occupant");
export const isOwnerPerson = (p) => !!p?.is_owner || ["owner", "co-owner", "landlord"].includes(String(p?.relationship || "").toLowerCase());
export const RELATIONSHIPS = ["Owner", "Tenant", "Occupant", "Spouse", "Family", "Other"];
export const PROPERTY_TYPES = ["Condo", "Single-family home", "Townhome", "Apartment", "Other"];
/* One property label everywhere: "2500 Longview Street 210" (street, then unit). */
export const propertyLabel = (h) => homeLabel(h);
export const cityLine = (h) => [h?.city, [h?.state, h?.zip].map((s) => String(s || "").trim()).filter(Boolean).join(" ")].map((s) => String(s || "").trim()).filter(Boolean).join(", ");
export const SINGLE_FAMILY = "Single-family home";
export const emptyHousehold = () => ({ unit: "", property_type: "", address: "", city: "", state: "", zip: "" });
export const emptyResident = (relationship = "Owner") => ({ first_name: "", last_name: "", phone: "", email: "", relationship, is_owner: relationship === "Owner", consent_captured: false, notes: "" });

/* Where a person stands with the concierge — ONE word the whole page agrees on. */
export function personStatus(p, delivery) {
  if (!p) return "not_invited";
  if (p.pending_approval) return "pending";
  if (p.active) return "active";
  if (p.opted_out || p.reach?.sms === "stop") return "stop";
  const inv = p.invited_at || p.invited_email_at;
  if (inv && delivery?.status === "failed") return "failed";
  if (inv) return "invited";
  return "not_invited";
}
export const STATUS_META = {
  active: { dot: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700 ring-emerald-200", word: "Active", title: "Has texted, called the concierge or signed into the portal" },
  invited: { dot: "bg-amber-400", soft: "bg-amber-50 text-amber-800 ring-amber-200", word: "Invited", title: "Welcome sent — waiting for them to text in" },
  not_invited: { dot: "bg-zinc-300", soft: "bg-zinc-100 text-zinc-600 ring-zinc-200", word: "Not invited", title: "No invite sent yet" },
  stop: { dot: "bg-red-600", soft: "bg-red-50 text-red-700 ring-red-200", word: "Opted out", title: "Texted STOP — the concierge can't text them" },
  failed: { dot: "bg-red-600", soft: "bg-red-50 text-red-700 ring-red-200", word: "Invite bounced", title: "The welcome text couldn't be delivered" },
  pending: { dot: "bg-amber-500", soft: "bg-amber-100 text-amber-900 ring-amber-300", word: "Waiting for approval", title: "Self-registered — confirm their residency" },
};
export const needsAttention = (s) => s === "stop" || s === "failed" || s === "pending";

export function StatusPill({ status, size = "sm", testid }) {
  const t = STATUS_META[status] || STATUS_META.not_invited;
  return <span data-testid={testid} title={t.title} className={`inline-flex items-center gap-1.5 rounded-full ring-1 font-bold whitespace-nowrap ${size === "sm" ? "px-2 h-6 text-[11px]" : "px-2.5 h-7 text-[12px]"} ${t.soft}`}><span className={`h-2 w-2 rounded-full ${t.dot}`} />{t.word}</span>;
}

/* ---- primitives (the approved C+ look) ---- */
const PALETTE = ["bg-rose-100 text-rose-900", "bg-amber-100 text-amber-900", "bg-emerald-100 text-emerald-900", "bg-sky-100 text-sky-900", "bg-teal-100 text-teal-900", "bg-orange-100 text-orange-900"];
const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); };
export function Avatar({ name, size = 36, className = "" }) {
  return <span className={`inline-grid place-items-center rounded-full font-bold shrink-0 ${PALETTE[hash(name || "?") % PALETTE.length]} ${className}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}>{initials(name)}</span>;
}

const REACH_TONE = { active: "text-emerald-600", invited: "text-amber-500", stop: "text-red-600", failed: "text-red-600", none: "text-zinc-300" };
const REACH_WORD = { active: "connected", invited: "invited, waiting", stop: "texted STOP", failed: "bounced", none: "not set up" };
export function Reach({ reach, failed = false, size = 15, gap = "gap-2.5", labels = false }) {
  const r = { ...(reach || {}) };
  if (failed && r.sms !== "active" && r.sms !== "stop") r.sms = "failed";
  const items = [["sms", MessageSquareText, "Text"], ["voice", PhoneCall, "Voice"], ["portal", KeyRound, "Portal"]];
  return (
    <span className={`inline-flex items-center ${gap}`} data-testid="reach">
      {items.map(([k, Icon, label]) => { const st = r[k] || "none"; return (
        <span key={k} title={`${label}: ${REACH_WORD[st]}`} className={`inline-flex items-center gap-1 ${REACH_TONE[st]}`}>
          <Icon style={{ width: size, height: size }} strokeWidth={st === "none" ? 1.5 : 2.2} />
          {labels && <span className={`text-[12px] font-semibold ${st === "none" ? "text-zinc-400" : ""}`}>{label}</span>}
        </span>
      ); })}
    </span>
  );
}

export function Btn({ variant = "outline", size = "md", className = "", children, ...rest }) {
  const v = { primary: "bg-redline-500 text-white hover:bg-redline-600", dark: "bg-zinc-900 text-white hover:bg-zinc-700", outline: "bg-white text-zinc-900 border border-zinc-200 hover:border-zinc-900", ghost: "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900", danger: "text-red-700 bg-red-50 hover:bg-red-100" }[variant];
  const s = { sm: "h-8 px-3 text-[12px] gap-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5", md: "h-11 px-4 text-[14px] gap-2 [&_svg]:h-4 [&_svg]:w-4" }[size];
  return <button type="button" className={`inline-flex items-center justify-center rounded-full font-bold whitespace-nowrap transition-colors disabled:opacity-50 disabled:pointer-events-none ${v} ${s} ${className}`} {...rest}>{children}</button>;
}

export const inp = "w-full h-11 rounded-xl border border-zinc-200 bg-white px-3.5 text-[14px] outline-none focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition disabled:bg-zinc-50 disabled:text-zinc-500";
export const inpSm = "rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-zinc-900";

export function Field({ label, hint, children, className = "" }) {
  return <label className={`block ${className}`}><span className="block text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-500 mb-1.5">{label}</span>{children}{hint && <span className="block mt-1.5 text-[12px] text-zinc-400">{hint}</span>}</label>;
}

export function Toggle({ on, onChange, label, testid, disabled }) {
  return <button type="button" disabled={disabled} data-testid={testid} onClick={() => onChange?.(!on)} aria-pressed={on} className="flex items-center gap-3 h-11 disabled:opacity-60"><span className={`relative inline-block h-6 w-11 rounded-full transition-colors ${on ? "bg-zinc-900" : "bg-zinc-200"}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "left-[22px]" : "left-0.5"}`} /></span><span className="text-[14px] font-semibold">{label}</span></button>;
}

export function Section({ title, action, children, testid }) {
  return <section data-testid={testid}><div className="flex items-center justify-between mb-3"><h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">{title}</h3>{action}</div>{children}</section>;
}

export function Card({ title, icon: Icon, action, children, className = "", testid }) {
  return (
    <section className={`rounded-2xl border border-zinc-200 bg-white ${className}`} data-testid={testid}>
      <header className="flex items-center justify-between gap-3 px-5 h-14 border-b border-zinc-100">
        <h2 className="inline-flex items-center gap-2 text-[15px] font-bold">{Icon && <Icon className="h-4 w-4 text-zinc-500" />}{title}</h2>
        {action}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function InfoBox({ label, value, mono = false, tone, className = "" }) {
  return (
    <div className={`rounded-xl border border-zinc-200 bg-zinc-50/60 px-3.5 py-3 min-w-0 ${className}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-400">{label}</div>
      <div className={`mt-1 text-[14px] font-bold truncate ${mono ? "font-mono" : ""} ${tone === "good" ? "text-emerald-700" : tone === "muted" ? "text-zinc-400" : "text-zinc-900"}`} title={typeof value === "string" ? value : undefined}>{value}</div>
    </div>
  );
}

export function Empty({ children, testid }) {
  return <p data-testid={testid} className="rounded-xl bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-[13px] text-zinc-500">{children}</p>;
}

/* US phone input — stored E.164 ("+15125550123"), typed as (512) 555-0123. */
export function PhoneField({ value, onChange, testid, placeholder = "(512) 555-0123", className = "" }) {
  let raw = String(value || "");
  if (raw.startsWith("+1")) raw = raw.slice(2);
  let d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  const a = d.slice(0, 3), b = d.slice(3, 6), c = d.slice(6, 10);
  const display = d.length > 6 ? `(${a}) ${b}-${c}` : d.length > 3 ? `(${a}) ${b}` : d.length > 0 ? `(${a}` : "";
  const onInput = (e) => {
    let typed = e.target.value.trim();
    if (typed.startsWith("+1")) typed = typed.slice(2);
    let digits = typed.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    onChange(digits ? "+1" + digits.slice(0, 10) : "");
  };
  return (
    <div className={`flex items-stretch h-11 rounded-xl border border-zinc-200 bg-white overflow-hidden focus-within:border-zinc-900 focus-within:ring-4 focus-within:ring-zinc-900/5 transition ${className}`}>
      <span className="flex items-center px-3 bg-zinc-50 border-r border-zinc-200 text-[13px] text-zinc-500 font-bold select-none">+1</span>
      <input data-testid={testid} inputMode="tel" autoComplete="tel-national" value={display} onChange={onInput} placeholder={placeholder} className="flex-1 min-w-0 px-3.5 text-[14px] font-mono outline-none bg-transparent" />
    </div>
  );
}

export function Kicker({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">{children}</div>;
}

export const TYPE_TONE = { owners: "bg-zinc-100 text-zinc-700", renters: "bg-zinc-100 text-zinc-700", board: "bg-amber-100 text-amber-900" };
export function TypeChips({ labels, keys = [], size = "sm", testid }) {
  if (!labels?.length) return <span className="text-[12px] text-zinc-300">—</span>;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 ${size === "sm" ? "flex-nowrap" : "flex-wrap"}`}>{labels.map((t, i) => <span key={t} className={`rounded-full font-bold whitespace-nowrap inline-flex items-center ${size === "sm" ? "px-2 h-5 text-[11px]" : "px-2.5 h-6 text-[12px]"} ${TYPE_TONE[keys[i]] || "bg-sky-100 text-sky-900"}`}>{t}</span>)}</span>;
}
